import { cn } from '@/lib/cn';
import type { JSX } from 'solid-js';
import angleSmallDown from './icons/angle-small-down.svg?raw';
import angleSmallLeft from './icons/angle-small-left.svg?raw';
import apple from './icons/apple.svg?raw';
import bracketsCurly from './icons/brackets-curly.svg?raw';
import calendar from './icons/calendar.svg?raw';
import check from './icons/check.svg?raw';
import checklistPlan from './icons/checklist-plan.svg?raw';
import compare from './icons/compare.svg?raw';
import computer from './icons/computer.svg?raw';
import copy from './icons/copy.svg?raw';
import crossCircle from './icons/cross-circle.svg?raw';
import crossSmall from './icons/cross-small.svg?raw';
import documentIcon from './icons/document.svg?raw';
import home from './icons/home.svg?raw';
import info from './icons/info.svg?raw';
import link from './icons/link.svg?raw';
import lock from './icons/lock.svg?raw';
import menuBurger from './icons/menu-burger.svg?raw';
import pencil from './icons/pencil.svg?raw';
import polling from './icons/polling.svg?raw';
import product from './icons/product.svg?raw';
import publish from './icons/publish.svg?raw';
import release from './icons/release.svg?raw';
import rotate from './icons/rotate.svg?raw';
import server from './icons/server.svg?raw';
import smartphone from './icons/smartphone.svg?raw';
import users from './icons/users.svg?raw';
import versionControl from './icons/version-control.svg?raw';

/**
 * icons/*.svg are mostly Flaticon UIcons, regular-rounded (line) style
 * (https://www.flaticon.com/uicons — attribution required, see Login footer),
 * plus ad-hoc release-menu icons. All must be fill-based with no hardcoded
 * fill/stroke so `currentColor` applies; each renders with its own viewBox.
 * Exception: brackets-curly is Lucide "braces" (ISC), stroked with currentColor.
 */
const parse = (svg: string): { viewBox: string; html: string } => ({
  viewBox: /viewBox="([^"]*)"/.exec(svg)?.[1] ?? '0 0 24 24',
  html: svg.slice(svg.indexOf('>', svg.indexOf('<svg')) + 1, svg.lastIndexOf('<')),
});

const ICONS = {
  calendar: parse(calendar),
  smartphone: parse(smartphone),
  users: parse(users),
  lock: parse(lock),
  'angle-small-left': parse(angleSmallLeft),
  'angle-small-down': parse(angleSmallDown),
  'menu-burger': parse(menuBurger),
  pencil: parse(pencil),
  document: parse(documentIcon),
  home: parse(home),
  'cross-small': parse(crossSmall),
  check: parse(check),
  'checklist-plan': parse(checklistPlan),
  compare: parse(compare),
  'cross-circle': parse(crossCircle),
  info: parse(info),
  copy: parse(copy),
  publish: parse(publish),
  link: parse(link),
  apple: parse(apple),
  'brackets-curly': parse(bracketsCurly),
  computer: parse(computer),
  product: parse(product),
  polling: parse(polling),
  release: parse(release),
  rotate: parse(rotate),
  server: parse(server),
  'version-control': parse(versionControl),
} as const;

export type IconName = keyof typeof ICONS;

export interface IconProps {
  name: IconName;
  class?: string;
}

export const Icon = (props: IconProps): JSX.Element => (
  <svg
    viewBox={ICONS[props.name].viewBox}
    fill="currentColor"
    class={cn('h-4 w-4 shrink-0', props.class)}
    data-icon={props.name}
    aria-hidden="true"
    innerHTML={ICONS[props.name].html}
  />
);
